import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { EntityLocationFields } from '@/components/ui/entity-location-fields'
import {
  useOrganisations,
  type Organisation,
  type OrganisationLocation,
} from '@/hooks/use-organisations'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import {
  isOrganisationLocationBlank,
  organisationLocations,
  organisationLocationsForSave,
} from '@/lib/organisation-locations'
import { entityCountryForEdit } from '@/utils/entity-location-fields'
import { shallowDraftEqual } from '@/utils/draft-equality'

type LocationDraft = {
  key: string
  city: string
  country: string
  description: string
}

function newDraftKey(): string {
  return `location-${crypto.randomUUID()}`
}

function emptyLocationDraft(): LocationDraft {
  return {
    key: newDraftKey(),
    city: '',
    country: '',
    description: '',
  }
}

function locationToDraft(location: OrganisationLocation): LocationDraft {
  return {
    key: location.id,
    city: location.city ?? '',
    country: entityCountryForEdit(location.country, CLIENT_LOCALE),
    description: location.description ?? '',
  }
}

function buildDrafts(organisation: Organisation): LocationDraft[] {
  const rows = organisationLocations(organisation)
  if (rows.length === 0) return [emptyLocationDraft()]
  return rows.map(locationToDraft)
}

interface OrganisationLocationsCanvasProps {
  slug: string
  organisation: Organisation
  onClose: () => void
  onSaved?: (updated: Organisation) => void
}

export function OrganisationLocationsCanvas({
  slug,
  organisation,
  onClose,
  onSaved,
}: OrganisationLocationsCanvasProps) {
  const { updateOrganisation, isUpdating, updateError } = useOrganisations()
  const [initialDrafts] = useState(() => buildDrafts(organisation))
  const [drafts, setDrafts] = useState<LocationDraft[]>(initialDrafts)
  const [validationError, setValidationError] = useState<string | null>(null)

  const isDirty = !shallowDraftEqual({ rows: drafts }, { rows: initialDrafts })
  const serverError = updateError instanceof Error ? updateError.message : updateError ? String(updateError) : null

  const patchDraft = (key: string, patch: Partial<LocationDraft>) => {
    setDrafts((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))
    setValidationError(null)
  }

  const handleAdd = () => {
    setDrafts((current) => [...current, emptyLocationDraft()])
    setValidationError(null)
  }

  const handleRemove = (key: string) => {
    setDrafts((current) => {
      const next = current.filter((row) => row.key !== key)
      return next.length > 0 ? next : [emptyLocationDraft()]
    })
    setValidationError(null)
  }

  const handleSave = () => {
    const tooLong = drafts.find((row) => row.description.trim().length > 255)
    if (tooLong) {
      setValidationError('Location description must be 255 characters or fewer')
      return
    }
    setValidationError(null)
    updateOrganisation(
      {
        slug,
        data: { locations: organisationLocationsForSave(drafts) },
      },
      {
        onSuccess: (updated) => {
          onSaved?.(updated)
          onClose()
        },
      },
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <p className="text-sm text-muted-foreground">
          Add one or more places. Country, city, and a short note are all optional.
        </p>
        {drafts.map((row, index) => (
          <fieldset key={row.key} className="space-y-4 rounded-lg border border-border p-4">
            <legend className="sr-only">Location {index + 1}</legend>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium">Location {index + 1}</p>
              <Button
                type="button"
                variant="ghostToolbar"
                size="iconSm"
                aria-label={`Remove location ${index + 1}`}
                title="Remove location"
                onClick={() => handleRemove(row.key)}
                disabled={drafts.length === 1 && isOrganisationLocationBlank(row)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <EntityLocationFields
              idPrefix={`org-location-${row.key}`}
              value={{ city: row.city, country: row.country }}
              onChange={(patch) => patchDraft(row.key, patch)}
            />
            <FormField
              label="Description"
              htmlFor={`org-location-${row.key}-description`}
              description="Optional. For example: Headquarters, warehouse, or remote hub."
            >
              <Input
                id={`org-location-${row.key}-description`}
                value={row.description}
                onChange={(event) => patchDraft(row.key, { description: event.target.value })}
                placeholder="Headquarters"
                maxLength={255}
              />
            </FormField>
          </fieldset>
        ))}
        <Button type="button" variant="outline" onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Add another location
        </Button>
        {validationError || serverError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {validationError ?? serverError}
          </div>
        ) : null}
      </form>
      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isUpdating}
            saveDisabled={!isDirty}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
